/**
 * Document Import API client methods.
 *
 * T014: Implement document upload, status, and extraction API calls
 */

import { API_CONFIG } from '../../config/api';
import { getToken } from '../tokenStorage';

export interface ExtractedPosition {
  symbol: string; // Required
  quantity: number; // Required (can be zero for user correction)

  // Optional fields - user fills in review screen
  cost_price?: number; // Optional now
  currency?: string; // Optional now
  date?: string; // Optional now - will default to today if missing
  asset_category?: string;
  close_price?: number;
  code?: string;
  confidence?: number;

  // Instrument resolution fields (set by frontend or auto-resolved by backend)
  instrument_id?: number; // ID of resolved instrument (manual or automatic)
  instrument_name?: string; // Auto-resolved instrument company name
  instrument_name_sch?: string; // Auto-resolved instrument name in Simplified Chinese
  instrument_name_tch?: string; // Auto-resolved instrument name in Traditional Chinese

  // Duplicate resolution fields (set by frontend)
  is_duplicate?: boolean; // Whether this position duplicates an existing position
  existing_position_id?: number; // ID of existing position to merge/replace
  resolution_action?: 'add' | 'replace'; // How to resolve duplicate

  // Reconciliation fields (import auto-reconcile feature)
  reconcile_transaction_type?: 'deposit' | 'withdrawal' | 'buy' | 'sell';
  existing_trade_quantity?: number; // FIFO-derived quantity, set by backend on GET
}

export interface ExtractedTrade {
  symbol: string; // Required
  quantity: number; // Required (can be zero for user correction)

  // Optional fields - user fills in review screen
  date?: string; // Optional now - will default to today if missing
  price?: number; // Optional now - frontend validates before save
  currency?: string; // Optional now
  asset_category?: string;
  close_price?: number;
  commission_fee?: number; // Optional
  code?: string;
  confidence?: number;

  // Instrument resolution fields (set by frontend or auto-resolved by backend)
  instrument_id?: number; // ID of resolved instrument (manual or automatic)
  instrument_name?: string; // Auto-resolved instrument company name
  instrument_name_sch?: string; // Auto-resolved instrument name in Simplified Chinese
  instrument_name_tch?: string; // Auto-resolved instrument name in Traditional Chinese

  // Duplicate detection fields (set by frontend)
  is_duplicate?: boolean; // Whether this trade is a duplicate within the same batch
}

export interface DocumentUploadRequest {
  file_data: string; // Base64-encoded file data
  file_type: 'image' | 'pdf';
  file_name?: string;
}

export interface DocumentUploadResponse {
  import_id: number;
  status: 'pending' | 'processing' | 'complete' | 'failed';
  message: string;
}

export interface DocumentStatusResponse {
  import_id: number;
  status: 'pending' | 'processing' | 'complete' | 'failed';
  positions_extracted: number;
  trades_extracted: number;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

export interface ExtractedDataResponse {
  positions: ExtractedPosition[];
  trades: ExtractedTrade[];
  confidence_scores: {
    positions: number;
    trades: number;
  };
}

export interface SaveExtractedDataRequest {
  positions: ExtractedPosition[];
  trades: ExtractedTrade[];
}

export interface SaveExtractedDataResponse {
  positions_created: number;
  trades_created: number;
  duplicates_skipped?: string[]; // List of duplicate trades that were skipped
}

/**
 * Upload a document (image or PDF) for AI processing.
 *
 * T093 [US5]: Upload document with base64 encoding for React Native compatibility.
 *
 * @param fileData - Base64-encoded file data
 * @param fileType - File type ('image' or 'pdf')
 * @param fileName - Original filename (optional)
 * @param signal - Optional AbortSignal to cancel the upload
 * @returns Promise<DocumentUploadResponse>
 */
export async function uploadDocument(
  fileData: string,
  fileType: 'image' | 'pdf',
  fileName?: string,
  signal?: AbortSignal
): Promise<DocumentUploadResponse> {
  const token = await getToken();

  const requestBody: DocumentUploadRequest = {
    file_data: fileData,
    file_type: fileType,
    file_name: fileName,
  };

  const response = await fetch(`${API_CONFIG.baseURL}/api/v1/document-import/upload`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
    signal, // Add abort signal support
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to upload document');
  }

  return response.json();
}

/**
 * Get the processing status of a document import.
 *
 * @param importId - Document import ID
 * @returns Promise<DocumentStatusResponse>
 */
export async function getDocumentStatus(importId: number): Promise<DocumentStatusResponse> {
  const token = await getToken();

  const response = await fetch(`${API_CONFIG.baseURL}/api/v1/document-import/${importId}/status`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to get document status');
  }

  return response.json();
}

/**
 * Get extracted positions and trades from a processed document.
 *
 * @param importId - Document import ID
 * @returns Promise<ExtractedDataResponse>
 */
export async function getExtractedData(importId: number): Promise<ExtractedDataResponse> {
  const token = await getToken();

  const response = await fetch(
    `${API_CONFIG.baseURL}/api/v1/document-import/${importId}/extracted`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to get extracted data');
  }

  return response.json();
}

/**
 * Save user-verified extracted data to the database.
 *
 * @param importId - Document import ID
 * @param data - Extracted positions and trades to save
 * @returns Promise<SaveExtractedDataResponse>
 */
export async function saveExtractedData(
  importId: number,
  data: SaveExtractedDataRequest
): Promise<SaveExtractedDataResponse> {
  const token = await getToken();

  const response = await fetch(`${API_CONFIG.baseURL}/api/v1/document-import/${importId}/save`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to save extracted data');
  }

  return response.json();
}

/**
 * Cancel a document import and discard extracted data.
 *
 * @param importId - Document import ID
 * @returns Promise<void>
 */
export async function cancelDocumentImport(importId: number): Promise<void> {
  const token = await getToken();

  const response = await fetch(`${API_CONFIG.baseURL}/api/v1/document-import/${importId}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: 'Failed to cancel document import' }));
    throw new Error(error.detail || 'Failed to cancel document import');
  }
}
